import ArrowBackLight from "@assets/navigation/back.svg";
import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import { invoiceServices } from "@services/apis/Invoice";
import {
  GetInvoiceApprovalsRequest,
  GetInvoiceApprovalsResponse,
  RequestsInvoiceData,
} from "@services/models/InvoiceApprovals/GetInvoiceApprovalsModel";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import webSocketService from "../../../services/websocket/WebsocketServices";
import ConstantFunctions from "../../../utils/constants_functions";
import InvoiceApprovalsDetails from "../../InvoiceApprovals/InvoiceApprovalsDetails";
import { ManagementHeaderNav } from "../../InvoiceApprovals/components/ManagementHeaderNav";
import { StatusPillRenderer } from "../../InvoiceApprovals/components/StatusPillRenderer";
import { StepPillRenderer } from "../../InvoiceApprovals/components/StepPillRenderer";

const MobileInvoiceApprovals = () => {
  const constantFunctions = new ConstantFunctions();

  const gridRef = useRef<AgGridReact>(null);
  const isMountedRef = useRef<boolean>(false);
  const hasInitialDataLoaded = useRef<boolean>(false);

  const [invoiceData, setInvoiceData] = useState<RequestsInvoiceData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const rowsPerPage = 1000;
  const [merchantIdSearch, setMerchantIdSearch] = useState<string>("");
  const [userIdSearch, setUserIdSearch] = useState<string>("");
  const [appliedMerchantId, setAppliedMerchantId] = useState<string>("");
  const [appliedUserId, setAppliedUserId] = useState<string>("");

  const [showDetailView, setShowDetailView] = useState<boolean>(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number>();
  const [selectedInvoiceData, setSelectedInvoiceData] =
    useState<RequestsInvoiceData>();

  const [filterStatus, setFilterStatus] = useState<string | undefined>();

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date",
        field: "submittedAt",
        flex: 0.8,
        minWidth: 90,
        maxWidth: 200,
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Merchant",
        field: "merchantName",
        flex: 0.8,
        minWidth: 120,
        maxWidth: 240,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "Settlement Type",
        field: "invoiceTypeDisplay",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 180,
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center h-full">
              <span className="text-[10px] text-text-base font-medium">
                {params.data.invoiceTypeDisplay || "Real Estate Invoice"}
              </span>
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Property Value",
        field: "unitPurchasePrice",
        flex: 1,
        minWidth: 110,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom header-left",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        cellRenderer: (params: any) => {
          return (
            <div className="flex flex-row gap-1 items-center">
              <img
                src={params.data.baseCurrencyImage}
                alt={params.data.baseCurrencySymbol}
                className="w-[12px] h-[12px] rounded"
              />
              <span className="text-[10px] text-text-base font-medium">
                {constantFunctions.formatCurrency(
                  params.data.requestData?.unitPurchasePrice!,
                )}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Base Amt",
        field: "invoiceDetails.baseAmount",
        flex: 0.4,
        minWidth: 100,
        maxWidth: 120,
        headerClass: "ag-header-cell-custom header-left",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        cellRenderer: (params: any) => {
          return (
            <div className="flex flex-row gap-1 items-center">
              <img
                src={params.data.baseCurrencyImage}
                alt={params.data.baseCurrencySymbol}
                className="w-[12px] h-[12px] rounded"
              />
              <span className="text-[10px] text-text-base font-medium">
                {constantFunctions.formatCurrency(
                  params.data.requestData?.baseAmount!,
                )}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Req. Amt",
        field: "settlementCurrencySymbol",
        flex: 1,
        minWidth: 110,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom header-left",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        cellRenderer: (params: any) => {
          const transactionDetails =
            params?.data?.invoiceDetails?.transactionDetails;

          return (
            <div className="flex flex-row gap-1 items-center">
              {transactionDetails?.settledCurrencyImage && (
                <img
                  src={transactionDetails.settledCurrencyImage}
                  alt=""
                  className="w-[12px] h-[12px] rounded"
                />
              )}

              <span className="text-[10px] text-text-base font-medium">
                {constantFunctions.formatCurrency(
                  transactionDetails?.settledAmountRequested ?? "0",
                )}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Amt Rec.",
        field: "settlementCurrencySymbol",
        flex: 1,
        minWidth: 110,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom header-left",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        cellRenderer: (params: any) => {
          const transactionDetails =
            params?.data?.invoiceDetails?.transactionDetails;
          return (
            <div className="flex flex-row gap-1 items-center">
              {transactionDetails?.settledCurrencyImage && (
                <img
                  src={transactionDetails.settledCurrencyImage}
                  alt=""
                  className="w-[12px] h-[12px] rounded"
                />
              )}
              <span className="text-[10px] text-text-base font-medium">
                {constantFunctions.formatCurrency(
                  params?.data?.invoiceDetails?.transactionDetails
                    ?.settledAmountReceived ?? "0",
                )}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Status",
        field: "settlementCurrencySymbol",
        flex: 1,
        minWidth: 110,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
        cellRenderer: (params: any) => {
          return (
            <StatusPillRenderer
              status={params.data.invoiceDetails?.transactionDetails?.status}
            />
          );
        },
      },
      {
        headerName: "Offramp Amt",
        field: "settlementCurrencySymbol",
        flex: 1,
        minWidth: 110,
        maxWidth: 180,
        headerClass: "ag-header-cell-custom header-left",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellStyle: () => ({
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
        }),
        cellRenderer: (params: any) => {
          return (
            <div className="flex flex-row gap-1 items-center">
              <img
                src={params.data.settlementCurrencyImage}
                alt={params.data.settlementCurrencySymbol}
                className="w-[12px] h-[12px] rounded"
              />
              <span className="text-[10px] text-text-base font-medium">
                {constantFunctions.formatCurrency(
                  params.data.transactionData?.remitFiatToMerchantBankAccount
                    ?.fromBankAccount?.amountSent || 0,
                )}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "S1",
        field: "transactionData.step",
        flex: 0.6,
        minWidth: 80,
        maxWidth: 90,
        headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        filter: null,
        cellRenderer: (params: any) => StepPillRenderer(params, 1),
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "S2",
        field: "transactionData.step",
        flex: 0.6,
        minWidth: 80,
        maxWidth: 90,
        headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        filter: null,
        cellRenderer: (params: any) => StepPillRenderer(params, 2),
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "S3",
        field: "transactionData.step",
        flex: 0.6,
        minWidth: 80,
        maxWidth: 90,
        headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        filter: null,
        cellRenderer: (params: any) => StepPillRenderer(params, 3),
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "S4",
        field: "transactionData.step",
        flex: 0.6,
        minWidth: 80,
        maxWidth: 90,
        headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        filter: null,
        cellRenderer: (params: any) => StepPillRenderer(params, 4),
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "S5",
        field: "transactionData.step",
        flex: 0.6,
        minWidth: 80,
        maxWidth: 90,
        headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        filter: null,
        cellRenderer: (params: any) => StepPillRenderer(params, 5),
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "S6",
        field: "transactionData.step",
        flex: 0.6,
        minWidth: 80,
        maxWidth: 90,
        headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        filter: null,
        cellRenderer: (params: any) => StepPillRenderer(params, 6),
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
      {
        headerName: "S7",
        field: "transactionData.step",
        flex: 0.6,
        minWidth: 80,
        maxWidth: 90,
        headerClass: "ag-header-cell-custom header-center",
        cellClass: "ag-cell-custom",
        filter: null,
        cellRenderer: (params: any) => StepPillRenderer(params, 7),
        cellStyle: () => ({
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }),
      },
    ],
    [constantFunctions],
  );

  const fetchInvoiceData = async (
    pageNumber = 1,
    append = false,
    merchantId?: string,
    userId?: string,
    showLoading: boolean = true,
  ) => {
    if (loading) return;

    try {
      if (showLoading) setLoading(true);
      const request: GetInvoiceApprovalsRequest = {
        page: pageNumber,
        limit: rowsPerPage,
        status: filterStatus,
      };

      if (merchantId && merchantId.trim()) {
        request.merchantId = merchantId.trim();
      }
      if (userId && userId.trim()) {
        request.userId = userId.trim();
      }

      const response: GetInvoiceApprovalsResponse =
        await invoiceServices.getInvoiceRequests(request);

      if (append) {
        setInvoiceData((prev) => [...prev, ...response.data.requests]);
      } else {
        setInvoiceData(response.data.requests);
      }
      setHasMore(response.data.requests.length === rowsPerPage);
    } catch (err: any) {
      setError("Failed to fetch invoice data");
    } finally {
      setLoading(false);
    }
  };

  const resetListState = useCallback(() => {
    setInvoiceData([]);
    setPage(1);
    setHasMore(true);
  }, []);

  const handleDataUpdate = useCallback(() => {
    fetchInvoiceData(
      1,
      false,
      appliedMerchantId || undefined,
      appliedUserId || undefined,
      false,
    );
    setPage(1);
    setHasMore(true);
  }, [appliedMerchantId, appliedUserId, filterStatus]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchInvoiceData(1, false);
    hasInitialDataLoaded.current = true;
  }, []);

  const handleInvoiceUpdateWebSocket = (socketData: RequestsInvoiceData) => {
    setInvoiceData((prevData) => {
      const updatedData = prevData.map((invoice) => {
        if (invoice.requestId === socketData.requestId) {
          return socketData;
        }
        return invoice;
      });
      return updatedData;
    });

    if (selectedInvoiceId === socketData.requestId) {
      setSelectedInvoiceData(socketData);
    }
  };

  useEffect(() => {
    const initializeWebSocket = async () => {
      try {
        webSocketService.onMessage(
          "admin_invoice_request_update",
          true,
          handleInvoiceUpdateWebSocket,
        );
      } catch (error) {
        console.error("WebSocket initialization error:", error);
      }
    };

    initializeWebSocket();
  }, [webSocketService.protectedSocket?.connected, selectedInvoiceId]);

  useEffect(() => {
    if (!isMountedRef.current) {
      return;
    }

    resetListState();
    fetchInvoiceData(
      1,
      false,
      appliedMerchantId || undefined,
      appliedUserId || undefined,
    );
  }, [appliedMerchantId, appliedUserId, resetListState]);

  const handleMerchantIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMerchantIdSearch(e.target.value);
  };

  const handleUserIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserIdSearch(e.target.value);
  };

  const handleMerchantSearch = useCallback(() => {
    const normalizedMerchantId = merchantIdSearch.trim();

    if (normalizedMerchantId === appliedMerchantId) {
      resetListState();
      fetchInvoiceData(
        1,
        false,
        normalizedMerchantId || undefined,
        appliedUserId || undefined,
      );
      return;
    }

    setAppliedMerchantId(normalizedMerchantId);
  }, [
    appliedMerchantId,
    appliedUserId,
    fetchInvoiceData,
    merchantIdSearch,
    resetListState,
  ]);

  const handleUserSearch = useCallback(() => {
    const normalizedUserId = userIdSearch.trim();

    if (normalizedUserId === appliedUserId) {
      resetListState();
      fetchInvoiceData(
        1,
        false,
        appliedMerchantId || undefined,
        normalizedUserId || undefined,
      );
      return;
    }

    setAppliedUserId(normalizedUserId);
  }, [
    appliedMerchantId,
    appliedUserId,
    fetchInvoiceData,
    resetListState,
    userIdSearch,
  ]);

  const handleMerchantClear = useCallback(() => {
    if (!merchantIdSearch && !appliedMerchantId) {
      return;
    }

    setMerchantIdSearch("");

    if (appliedMerchantId === "") {
      resetListState();
      fetchInvoiceData(1, false, undefined, appliedUserId || undefined);
      return;
    }

    setAppliedMerchantId("");
  }, [
    appliedMerchantId,
    appliedUserId,
    fetchInvoiceData,
    merchantIdSearch,
    resetListState,
  ]);

  const handleUserClear = useCallback(() => {
    if (!userIdSearch && !appliedUserId) {
      return;
    }

    setUserIdSearch("");

    if (appliedUserId === "") {
      resetListState();
      fetchInvoiceData(1, false, appliedMerchantId || undefined, undefined);
      return;
    }

    setAppliedUserId("");
  }, [
    appliedMerchantId,
    appliedUserId,
    fetchInvoiceData,
    resetListState,
    userIdSearch,
  ]);

  const loadMoreData = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchInvoiceData(
        nextPage,
        true,
        appliedMerchantId || undefined,
        appliedUserId || undefined,
      );
    }
  }, [appliedMerchantId, appliedUserId, loading, hasMore, page]);

  const onRowClicked = (event: RowClickedEvent) => {
    const selectedData = event.data as RequestsInvoiceData;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${event.rowIndex}"]`,
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    setSelectedInvoiceId(selectedData.requestId);
    setSelectedInvoiceData(selectedData);
    setShowDetailView(true);
  };

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasMore || loading) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        loadMoreData();
      }
    },
    [hasMore, loading, loadMoreData],
  );

  useEffect(() => {
    if (!isMountedRef.current || !hasInitialDataLoaded.current) {
      return;
    }

    setInvoiceData([]);
    setPage(1);
    setHasMore(true);
    fetchInvoiceData(
      1,
      false,
      appliedMerchantId || undefined,
      appliedUserId || undefined,
    );
  }, [appliedMerchantId, appliedUserId, filterStatus]);

  if (showDetailView && selectedInvoiceId && selectedInvoiceData) {
    return (
      <div className="flex flex-col h-full">
        {/* Detail Header with back button */}
        <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 px-4 w-full shrink-0">
          <div className="flex gap-2 items-center w-full">
            <img
              src={ArrowBackLight}
              alt="Back"
              className="cursor-pointer"
              onClick={() => setShowDetailView(false)}
            />
            <p className="flex text-text-base text-[13px] font-semibold items-center">
              Invoice Details (#{selectedInvoiceId})
            </p>
          </div>
        </div>

        {/* Detail Content */}
        <div className="flex-1 overflow-auto p-3">
          <InvoiceApprovalsDetails
            data={selectedInvoiceData}
            onDataUpdate={handleDataUpdate}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="h-[56px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 px-4 w-full shrink-0">
        <div className="flex flex-grow justify-between items-center w-full">
          <p className="flex text-text-base text-[13px] font-semibold items-center">
            Off Ramp Management
          </p>
          <ManagementHeaderNav />
        </div>
      </div>

      <div className="flex flex-col flex-1 overflow-auto">
        {/* Filters + Search stacked vertically */}
        <div className="flex flex-col gap-3 p-3">
          {/* Filter pills */}
          <div className="flex flex-row items-center gap-2 flex-wrap">
            <p className="text-text-base text-[10px] font-bold">Filter by:</p>
            {["pending", "approved", "rejected"].map((type) => (
              <div
                key={type}
                onClick={
                  loading
                    ? undefined
                    : () =>
                        setFilterStatus((prev) =>
                          prev === type ? undefined : type,
                        )
                }
                className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                } ${
                  filterStatus === type
                    ? "border-border-button-secondary bg-bg-button-secondary"
                    : "border-border-subtle bg-bg-page-body"
                }`}
              >
                {type}
              </div>
            ))}
          </div>

          {/* Search fields full-width stacked */}
          <div className="flex flex-col gap-2 w-full">
            <GlobalSearchInputField
              value={merchantIdSearch}
              onChange={handleMerchantIdChange}
              onSearch={handleMerchantSearch}
              onClear={handleMerchantClear}
              disabled={loading}
              isSearchApplied={Boolean(appliedMerchantId)}
              disableSearchButton={!merchantIdSearch.trim()}
              placeholder="Merchant ID"
            />
            <GlobalSearchInputField
              value={userIdSearch}
              onChange={handleUserIdChange}
              onSearch={handleUserSearch}
              onClear={handleUserClear}
              disabled={loading}
              isSearchApplied={Boolean(appliedUserId)}
              disableSearchButton={!userIdSearch.trim()}
              placeholder="User ID"
            />
          </div>
        </div>

        {/* AG Grid Table with horizontal scroll */}
        <div className="flex-1 px-3 pb-3 overflow-x-auto" style={{ minHeight: "400px" }}>
          {loading && invoiceData.length === 0 ? (
            <AgGridSkeleton
              columnDefs={columnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <AgGridReactTable
              ref={gridRef}
              rowData={invoiceData}
              columnDefs={columnDefs}
              onRowClicked={onRowClicked}
              onBodyScroll={onBodyScroll}
              getRowId={(params) => params.data.requestId?.toString()}
              error={error}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileInvoiceApprovals;
